import React, { useState, useEffect, useRef } from 'react';
import { Plus, ChevronLeft, ChevronRight, Minus, Phone, Mail, Clock, Building, Users, User, Contact, Settings, X, CheckCircle, AlertCircle } from 'lucide-react';
import apiClient from '../../../services/apiClient';
import { patientService } from '../../patient/services/patientService';

const AdminClinicsPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Admin Clinics';
  }, []);

  // State for clinic data
  const [clinicsData, setClinicsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedClinic, setExpandedClinic] = useState(null);

  // Toast notification state
  const [toasts, setToasts] = useState([]);
  
  // Edit state management
  const [editingSection, setEditingSection] = useState(null); // 'clinic-details', 'contact-details', 'operating-hours'
  const [editingClinicId, setEditingClinicId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  
  // Add Clinic Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [addClinicData, setAddClinicData] = useState({
    clinic_name: '',
    clinic_address: '',
    suburb: '',
    state: '',
    postcode: '',
    country: '',
    phone: '',
    email: '',
    services: '',
    primary_contact_name: '',
    primary_contact_phone: '',
    primary_contact_email: '',
    accounts_contact_name: '',
    accounts_contact_phone: '',
    accounts_contact_email: '',
    billing_email: '',
    open_public_holidays: false,
    closed_public_holidays: false
  });
  const [addLoading, setAddLoading] = useState(false);

  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState('');
  const [filterByPostcode, setFilterByPostcode] = useState('');
  const [filterByState, setFilterByState] = useState('');
  const [filterBySuburb, setFilterBySuburb] = useState('');
  const [filterByCountry, setFilterByCountry] = useState('');
  const [sortBy, setSortBy] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Dropdown states
  const [showIncludeInactiveDropdown, setShowIncludeInactiveDropdown] = useState(false);
  const [showPostcodeDropdown, setShowPostcodeDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showSuburbDropdown, setShowSuburbDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Refs for dropdowns
  const includeInactiveRef = useRef(null);
  const postcodeRef = useRef(null);
  const stateRef = useRef(null);
  const suburbRef = useRef(null);
  const countryRef = useRef(null);
  const sortRef = useRef(null);

  // Fetch clinics data
  const fetchClinics = async () => {
    try {
      setLoading(true);
      setError(null);
      const clinics = await patientService.getAllClinics();
      setClinicsData(clinics);
    } catch (err) {
      setError(err.message || 'Failed to fetch clinics');
      console.error('Error fetching clinics:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch clinics on component mount
  useEffect(() => {
    fetchClinics();
  }, []);

  // Helper functions to get unique values for dropdowns
  const getUniqueStates = React.useMemo(() => {
    return [...new Set(clinicsData.map(clinic => clinic.state))].filter(Boolean).sort();
  }, [clinicsData]);

  const getUniqueSuburbs = React.useMemo(() => {
    return [...new Set(clinicsData.map(clinic => clinic.suburb))].filter(Boolean).sort();
  }, [clinicsData]);

  const getUniqueCountries = React.useMemo(() => {
    return [...new Set(clinicsData.map(clinic => clinic.country))].filter(Boolean).sort();
  }, [clinicsData]);

  const getUniquePostcodes = React.useMemo(() => {
    return [...new Set(clinicsData.map(clinic => clinic.postcode))].filter(Boolean).sort();
  }, [clinicsData]);

  // Toast notification functions
  const addToast = (message, type = 'success') => {
    const id = Date.now();
    const newToast = { id, message, type };
    setToasts(prev => [...prev, newToast]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Filter and search logic
  const filteredClinics = React.useMemo(() => {
    let filtered = [...clinicsData];

    // Search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(clinic => 
        clinic.clinic_name?.toLowerCase().includes(searchLower) ||
        clinic.clinic_address?.toLowerCase().includes(searchLower) ||
        clinic.postcode?.toLowerCase().includes(searchLower)
      );
    }

    // Postcode filter
    if (filterByPostcode) {
      filtered = filtered.filter(clinic => 
        clinic.postcode?.toLowerCase().includes(filterByPostcode.toLowerCase())
      );
    }

    // State filter
    if (filterByState) {
      filtered = filtered.filter(clinic => 
        clinic.state?.toLowerCase().includes(filterByState.toLowerCase())
      );
    }

    // Suburb filter
    if (filterBySuburb) {
      filtered = filtered.filter(clinic => 
        clinic.suburb?.toLowerCase().includes(filterBySuburb.toLowerCase())
      );
    }

    // Country filter
    if (filterByCountry) {
      filtered = filtered.filter(clinic => 
        clinic.country?.toLowerCase().includes(filterByCountry.toLowerCase())
      );
    }

    // Sort logic
    if (sortBy) {
      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'Name (A-Z)':
            return (a.clinic_name || '').localeCompare(b.clinic_name || '');
          case 'Name (Z-A)':
            return (b.clinic_name || '').localeCompare(a.clinic_name || '');
          case 'Date Added':
            return new Date(b.created_at || 0) - new Date(a.created_at || 0);
          case 'Postcode':
            return (a.postcode || '').localeCompare(b.postcode || '');
          default:
            return 0;
        }
      });
    }

    return filtered;
  }, [clinicsData, searchTerm, filterByPostcode, filterByState, filterBySuburb, filterByCountry, sortBy]);

  // Pagination logic with filtered results
  const totalPages = Math.ceil(filteredClinics.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentClinics = filteredClinics.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterByPostcode, filterByState, filterBySuburb, filterByCountry, sortBy]);

  // Handle dropdown toggles
  const toggleIncludeInactiveDropdown = () => {
    setShowIncludeInactiveDropdown(!showIncludeInactiveDropdown);
    setShowPostcodeDropdown(false);
    setShowStateDropdown(false);
    setShowSuburbDropdown(false);
    setShowCountryDropdown(false);
    setShowSortDropdown(false);
  };

  const togglePostcodeDropdown = () => {
    setShowPostcodeDropdown(!showPostcodeDropdown);
    setShowIncludeInactiveDropdown(false);
    setShowStateDropdown(false);
    setShowSuburbDropdown(false);
    setShowCountryDropdown(false);
    setShowSortDropdown(false);
  };

  const toggleStateDropdown = () => {
    setShowStateDropdown(!showStateDropdown);
    setShowIncludeInactiveDropdown(false);
    setShowPostcodeDropdown(false);
    setShowSuburbDropdown(false);
    setShowCountryDropdown(false);
    setShowSortDropdown(false);
  };

  const toggleSuburbDropdown = () => {
    setShowSuburbDropdown(!showSuburbDropdown);
    setShowIncludeInactiveDropdown(false);
    setShowPostcodeDropdown(false);
    setShowStateDropdown(false);
    setShowCountryDropdown(false);
    setShowSortDropdown(false);
  };

  const toggleCountryDropdown = () => {
    setShowCountryDropdown(!showCountryDropdown);
    setShowIncludeInactiveDropdown(false);
    setShowPostcodeDropdown(false);
    setShowStateDropdown(false);
    setShowSuburbDropdown(false);
    setShowSortDropdown(false);
  };

  const toggleSortDropdown = () => {
    setShowSortDropdown(!showSortDropdown);
    setShowIncludeInactiveDropdown(false);
    setShowPostcodeDropdown(false);
    setShowStateDropdown(false);
    setShowSuburbDropdown(false);
    setShowCountryDropdown(false);
  };

  // Handle dropdown selections
  const handleIncludeInactiveSelect = (option) => {
    setIncludeInactive(option);
    setShowIncludeInactiveDropdown(false);
  };

  const handlePostcodeSelect = (postcode) => {
    setFilterByPostcode(postcode);
    setShowPostcodeDropdown(false);
  };

  const handleStateSelect = (state) => {
    setFilterByState(state);
    setShowStateDropdown(false);
  };

  const handleSuburbSelect = (suburb) => {
    setFilterBySuburb(suburb);
    setShowSuburbDropdown(false);
  };

  const handleCountrySelect = (country) => {
    setFilterByCountry(country);
    setShowCountryDropdown(false);
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setIncludeInactive('');
    setFilterByPostcode('');
    setFilterByState('');
    setFilterBySuburb('');
    setFilterByCountry('');
    setSortBy('');
    setCurrentPage(1);
  };

  const handleSortSelect = (sortOption) => {
    setSortBy(sortOption);
    setShowSortDropdown(false);
  };



  // Handle clinic actions - toggle expanded state
  const handleClinicAction = (clinicId) => {
    setExpandedClinic(expandedClinic === clinicId ? null : clinicId);
  };

  // Edit functionality
  const startEdit = (section, clinicId, initialData) => {
    setEditingSection(section);
    setEditingClinicId(clinicId);
    setEditFormData(initialData);
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setEditingClinicId(null);
    setEditFormData({});
  };

  const updateEditFormData = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveChanges = async (section, clinicId) => {
    try {
      setSaveLoading(true);
      
      let response;
      
      if (section === 'operating-hours') {
        // Extract operating hours data and public holiday settings
        const { open_public_holidays, closed_public_holidays, ...operatingHoursData } = editFormData;
        
        // First save operating hours
        response = await apiClient.put(`/clinics/${clinicId}/operating-hours`, operatingHoursData);

        // Then save public holiday settings to clinic
        const publicHolidayResponse = await apiClient.put(`/clinics/${clinicId}`, {
          open_public_holidays,
          closed_public_holidays
        });

        // Refetch all clinic data to ensure consistency
        await fetchClinics();
      } else {
        // For clinic and contact details, send to the main clinic endpoint
        response = await apiClient.put(`/clinics/${clinicId}`, editFormData);

        // For clinic/contact details, update the local state
        const updatedClinic = response.data;
        setClinicsData(prevClinics => 
          prevClinics.map(clinic => 
            clinic.id === clinicId ? { ...clinic, ...updatedClinic } : clinic
          )
        );
      }

      // Exit edit mode
      cancelEdit();

      // Show success message
      addToast('Clinic details updated successfully!', 'success');
      
    } catch (error) {
      console.error('Error saving changes:', error);
      addToast(`Failed to save changes: ${error.message}`, 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  // Add Clinic Modal Functions
  const openAddModal = () => {
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddClinicData({
      clinic_name: '',
      clinic_address: '',
      suburb: '',
      state: '',
      postcode: '',
      country: '',
      phone: '',
      email: '',
      services: '',
      primary_contact_name: '',
      primary_contact_phone: '',
      primary_contact_email: '',
      accounts_contact_name: '',
      accounts_contact_phone: '',
      accounts_contact_email: '',
      billing_email: '',
      open_public_holidays: false,
      closed_public_holidays: false
    });
  };

  const updateAddClinicData = (field, value) => {
    setAddClinicData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddClinic = async () => {
    try {
      setAddLoading(true);

      const response = await apiClient.post('/clinics/', addClinicData);
      const newClinic = response.data;
      
      // Add the new clinic to the local state
      setClinicsData(prevClinics => [newClinic, ...prevClinics]);
      
      // Close modal and reset form
      closeAddModal();
      
      // Show success message
      addToast('Clinic created successfully! The new clinic has been added to your list.', 'success');
      
    } catch (error) {
      console.error('Error creating clinic:', error);
      addToast(`Failed to create clinic: ${error.message}`, 'error');
    } finally {
      setAddLoading(false);
    }
  };

  // Handle pagination
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (includeInactiveRef.current && !includeInactiveRef.current.contains(event.target)) {
        setShowIncludeInactiveDropdown(false);
      }
      if (postcodeRef.current && !postcodeRef.current.contains(event.target)) {
        setShowPostcodeDropdown(false);
      }
      if (stateRef.current && !stateRef.current.contains(event.target)) {
        setShowStateDropdown(false);
      }
      if (suburbRef.current && !suburbRef.current.contains(event.target)) {
        setShowSuburbDropdown(false);
      }
      if (countryRef.current && !countryRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Operating Hours Section Component
  const OperatingHoursSection = ({ 
    clinic, 
    editingSection, 
    editingClinicId, 
    editFormData, 
    updateEditFormData, 
    startEdit, 
    cancelEdit, 
    saveChanges, 
    saveLoading 
  }) => {
    const [operatingHours, setOperatingHours] = useState({});
    const [loadingHours, setLoadingHours] = useState(false);

    const daysOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    useEffect(() => {
      const fetchOperatingHours = async () => {
        try {
          setLoadingHours(true);
          const response = await apiClient.get(`/clinics/${clinic.id}/operating-hours`);
          const hoursData = response.data;
          setOperatingHours(hoursData);
        } catch (error) {
          console.error('Error fetching operating hours:', error);
        } finally {
          setLoadingHours(false);
        }
      };

      if (clinic.id) {
        fetchOperatingHours();
      }
    }, [clinic.id]);

    const getTimeValue = (day, type) => {
      // If editing, get from edit form data, otherwise from operating hours
      if (editingSection === 'operating-hours' && editingClinicId === clinic.id) {
        const editDayData = editFormData[day];
        if (!editDayData) return '';
        if (editDayData.is_closed) return '';
        return editDayData[type] || '';
      }
      
      const dayData = operatingHours[day];
      if (!dayData) return '';
      if (dayData.is_closed) return '';
      return dayData[type] || '';
    };

    const isEditing = editingSection === 'operating-hours' && editingClinicId === clinic.id;

    const handleTimeChange = (day, type, value) => {
      const currentDayData = editFormData[day] || {};
      updateEditFormData(day, {
        ...currentDayData,
        [type]: value,
        is_closed: false
      });
    };

    const handleClosedToggle = (day, isClosed) => {
      const currentDayData = editFormData[day] || {};
      updateEditFormData(day, {
        ...currentDayData,
        is_closed: isClosed,
        opening_time: isClosed ? '' : currentDayData.opening_time || '',
        closing_time: isClosed ? '' : currentDayData.closing_time || ''
      });
    };

    const prepareEditData = () => {
      const editData = {};
      daysOrder.forEach(day => {
        const dayData = operatingHours[day] || {};
        editData[day] = {
          opening_time: dayData.opening_time || '',
          closing_time: dayData.closing_time || '',
          is_closed: dayData.is_closed || false
        };
      });
      // Add public holiday settings
      editData.open_public_holidays = clinic.open_public_holidays || false;
      editData.closed_public_holidays = clinic.closed_public_holidays || false;
      return editData;
    };

    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="bg-blue-600 text-white px-3 py-1.5">
          <h3 className="text-xs font-medium flex items-center">
            <Clock className="h-3 w-3 mr-2" />
            Operating Hours
          </h3>
        </div>
        <div className="p-3">
          {loadingHours ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-1.5 px-2 font-medium text-gray-700 text-xs">Day</th>
                    {daysOrder.map(day => (
                      <th key={day} className="text-left py-1.5 px-2 font-medium text-gray-700 text-xs">{day}</th>
                    ))}
                    <th className="text-left py-1.5 px-2 font-medium text-gray-700 text-xs">Public Holidays</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="py-1.5 px-2 font-medium text-gray-700 text-xs">Opening Time</td>
                    {daysOrder.map(day => (
                      <td key={day} className="py-1.5 px-2">
                        <input 
                          type="time" 
                          value={getTimeValue(day, 'opening_time')} 
                          onChange={(e) => isEditing && handleTimeChange(day, 'opening_time', e.target.value)}
                          readOnly={!isEditing} 
                          disabled={isEditing && editFormData[day]?.is_closed}
                          className={`w-full px-2 py-1 text-xs border border-gray-300 rounded ${
                            isEditing && !editFormData[day]?.is_closed
                              ? 'bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
                              : 'bg-gray-50'
                          }`}
                        />
                      </td>
                    ))}
                    <td className="py-1.5 px-2">
                      <div className="flex items-center space-x-2">
                        <input 
                          type="checkbox" 
                          checked={
                            isEditing 
                              ? editFormData.open_public_holidays || false
                              : clinic.open_public_holidays || false
                          }
                          onChange={(e) => {
                            if (isEditing) {
                              if (e.target.checked) {
                                updateEditFormData('open_public_holidays', true);
                                updateEditFormData('closed_public_holidays', false);
                              } else {
                                updateEditFormData('open_public_holidays', false);
                              }
                            }
                          }}
                          readOnly={!isEditing}
                          className="rounded" 
                        />
                        <span className="text-xs">Open Public Holiday</span>
                      </div>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-1.5 px-2 font-medium text-gray-700 text-xs">Closing Time</td>
                    {daysOrder.map(day => (
                      <td key={day} className="py-1.5 px-2">
                        <input 
                          type="time" 
                          value={getTimeValue(day, 'closing_time')} 
                          onChange={(e) => isEditing && handleTimeChange(day, 'closing_time', e.target.value)}
                          readOnly={!isEditing} 
                          disabled={isEditing && editFormData[day]?.is_closed}
                          className={`w-full px-2 py-1 text-xs border border-gray-300 rounded ${
                            isEditing && !editFormData[day]?.is_closed
                              ? 'bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
                              : 'bg-gray-50'
                          }`}
                        />
                      </td>
                    ))}
                    <td className="py-1.5 px-2">
                      <div className="flex items-center space-x-2">
                        <input 
                          type="checkbox" 
                          checked={
                            isEditing 
                              ? editFormData.closed_public_holidays || false
                              : clinic.closed_public_holidays || false
                          }
                          onChange={(e) => {
                            if (isEditing) {
                              if (e.target.checked) {
                                updateEditFormData('closed_public_holidays', true);
                                updateEditFormData('open_public_holidays', false);
                              } else {
                                updateEditFormData('closed_public_holidays', false);
                              }
                            }
                          }}
                          readOnly={!isEditing}
                          className="rounded" 
                        />
                        <span className="text-xs">Closed Public Holiday</span>
                      </div>
                    </td>
                  </tr>
                  {isEditing && (
                    <tr>
                      <td className="py-1.5 px-2 font-medium text-gray-700 text-xs">Closed</td>
                      {daysOrder.map(day => (
                        <td key={day} className="py-1.5 px-2">
                          <div className="flex items-center justify-center">
                            <input 
                              type="checkbox"
                              checked={editFormData[day]?.is_closed || false}
                              onChange={(e) => handleClosedToggle(day, e.target.checked)}
                              className="rounded" 
                            />
                          </div>
                        </td>
                      ))}
                      <td className="py-2 px-3"></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex justify-end mt-3 space-x-2">
            {isEditing ? (
              <>
                <button 
                  onClick={cancelEdit}
                  disabled={saveLoading}
                  className="px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => saveChanges('operating-hours', clinic.id)}
                  disabled={saveLoading}
                  className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {saveLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
              </>
            ) : (
              <button 
                onClick={() => startEdit('operating-hours', clinic.id, prepareEditData())}
                className="px-3 py-1.5 text-xs text-blue-600 hover:text-blue-800"
              >
                Edit Record
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Clinician Carousel Component
  const ClinicianCarousel = ({ clinicId }) => {
    const [clinicians, setClinicians] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loadingClinicians, setLoadingClinicians] = useState(false);

    const cardsToShow = 4; // Number of cards to show at once

    // Fetch clinicians for this clinic
    useEffect(() => {
      const fetchClinicians = async () => {
        try {
          setLoadingClinicians(true);
          // Use the existing clinicians API endpoint
          const response = await apiClient.get(`/clinicians/by-clinic/${clinicId}`);
          const cliniciansData = response.data;
          
          // Transform the data to match our component needs
          const transformedClinicians = cliniciansData.map(clinician => {
            return {
            id: clinician.id,
            name: clinician.full_name || 
                  (clinician.firstname && clinician.lastname ? `${clinician.firstname} ${clinician.lastname}` : '') ||
                  (clinician.first_name && clinician.last_name ? `${clinician.first_name} ${clinician.last_name}` : '') ||
                  'Unknown Clinician',
            specialties: Array.isArray(clinician.specialties) ? clinician.specialties : (clinician.specialties ? clinician.specialties.split(',').map(s => s.trim()) : ['General Practice']),
            phone: clinician.phone || 'Not provided',
            email: clinician.email || 'Not provided',
            qualifications: clinician.qualifications || '',
            experienceYears: clinician.experience_years || 0,
            isAvailable: clinician.is_available,
            clinicianId: clinician.clinician_id || '',
            bio: clinician.bio || '',
            isPrimary: clinician.is_primary_clinic || false
            };
          });
          
          setClinicians(transformedClinicians);
        } catch (error) {
          console.error('Error fetching clinicians:', error);
          // Fallback to empty array on error
          setClinicians([]);
        } finally {
          setLoadingClinicians(false);
        }
      };

      fetchClinicians();
    }, [clinicId]);

    const handlePrevious = () => {
      setCurrentIndex(prev => Math.max(0, prev - 1));
    };

    const handleNext = () => {
      setCurrentIndex(prev => Math.min(prev + 1, Math.max(0, clinicians.length - cardsToShow)));
    };

    const canGoNext = currentIndex < Math.max(0, clinicians.length - cardsToShow);
    const canGoPrev = currentIndex > 0;

    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="bg-blue-600 text-white px-3 py-1.5">
          <h3 className="text-xs font-medium flex items-center">
            <Users className="h-3 w-3 mr-2" />
            Clinicians
          </h3>
        </div>
        <div className="p-3">
          {loadingClinicians ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            </div>
          ) : clinicians.length === 0 ? (
            <div className="text-center py-12">
              <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m9 5.197v1M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">No Clinicians Found</h3>
              <p className="text-xs text-gray-500">No clinicians are currently associated with this clinic.</p>
            </div>
          ) : (
            <div className="relative">
              {/* Navigation Arrows */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={handlePrevious}
                  disabled={!canGoPrev}
                  className={`p-2 rounded-md border transition-colors ${
                    canGoPrev 
                      ? 'border-gray-300 bg-white hover:bg-gray-50 text-gray-700' 
                      : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                
                <span className="text-sm text-gray-600">
                  {currentIndex + 1}-{Math.min(currentIndex + cardsToShow, clinicians.length)} of {clinicians.length}
                </span>
                
                <button
                  onClick={handleNext}
                  disabled={!canGoNext}
                  className={`p-2 rounded-md border transition-colors ${
                    canGoNext 
                      ? 'border-gray-300 bg-white hover:bg-gray-50 text-gray-700' 
                      : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* Clinician Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {clinicians.slice(currentIndex, currentIndex + cardsToShow).map((clinician, index) => (
                  <div key={clinician.id} className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-4 border border-blue-200 shadow-sm hover:shadow-md transition-shadow duration-200">
                    
                    {/* Header with name and status */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-800 text-sm flex items-center">
                          <User className="h-4 w-4 mr-2 text-blue-600" />
                          {clinician.name}
                        </h4>
                        {clinician.isPrimary && (
                          <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">Primary</span>
                        )}
                      </div>
                      {clinician.clinicianId && (
                        <p className="text-xs text-gray-500 ml-6 mr-4">
                          ID: {clinician.clinicianId}
                        </p>
                      )}
                    </div>
                    
                    {/* Specialties */}
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-1">
                        {clinician.specialties.map((specialty, idx) => (
                          <span key={idx} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-md">
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {/* Qualifications */}
                    {clinician.qualifications && (
                      <div className="mb-3">
                        <p className="text-xs text-gray-600">
                          <span className="font-medium">Qualifications:</span> {clinician.qualifications}
                        </p>
                      </div>
                    )}
                    
                    {/* Experience */}
                    {clinician.experienceYears > 0 && (
                      <div className="mb-3">
                        <p className="text-xs text-gray-600">
                          <span className="font-medium">Experience:</span> {clinician.experienceYears} years
                        </p>
                      </div>
                    )}
                    
                    {/* Contact Information */}
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center text-xs text-gray-600">
                        <Phone className="h-3 w-3 text-blue-500 mr-2 flex-shrink-0" />
                        <span className="truncate">{clinician.phone}</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <Mail className="h-3 w-3 text-blue-500 mr-2 flex-shrink-0" />
                        <span className="truncate">{clinician.email}</span>
                      </div>
                    </div>
                    
                    {/* Bio */}
                    {clinician.bio && (
                      <div className="mb-3">
                        <p className="text-xs text-gray-600 line-clamp-2">{clinician.bio}</p>
                      </div>
                    )}
                    
                    {/* Availability Status */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full mr-2 bg-green-500"></div>
                        <span className="text-xs font-medium text-green-700">
                          Available
                        </span>
                      </div>
                    </div>
                    
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Blue header */}
      <div className="bg-blue-600 text-white py-1 px-2">
        <h1 className="text-xl font-bold text-center">Clinics</h1>
      </div>

      <div className="flex">
        {/* Left Column - Filters (25%) */}
        <div className="w-1/4 bg-gray-50 min-h-screen p-4">
          <div className="bg-white rounded-lg border border-blue-200 p-3 mt-3">
            <div className="space-y-3">
              {/* Add Clinic Button */}
              <div>
                <button
                  onClick={openAddModal}
                  className="w-full px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm flex items-center justify-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Clinic
                </button>
              </div>

              {/* Search */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Search:
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Include Inactive */}
              <div className="relative" ref={includeInactiveRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Include Inactive:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleIncludeInactiveDropdown}
                >
                  <span className={includeInactive ? 'text-gray-900' : 'text-gray-500'}>
                    {includeInactive || 'Yes / No'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showIncludeInactiveDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
                    {['Yes', 'No'].map((option) => (
                      <div
                        key={option}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleIncludeInactiveSelect(option)}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Filter By Postcode */}
              <div className="relative" ref={postcodeRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Filter By Postcode:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={togglePostcodeDropdown}
                >
                  <span className={filterByPostcode ? 'text-gray-900' : 'text-gray-500'}>
                    {filterByPostcode || 'Please Select'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showPostcodeDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-32 overflow-y-auto">
                    {[...new Set(clinicsData.map(clinic => clinic.postcode))].filter(Boolean).map((postcode) => (
                      <div
                        key={postcode}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handlePostcodeSelect(postcode)}
                      >
                        {postcode}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Filter By State */}
              <div className="relative" ref={stateRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Filter By State:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleStateDropdown}
                >
                  <span className={filterByState ? 'text-gray-900' : 'text-gray-500'}>
                    {filterByState || 'Please Select'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showStateDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-32 overflow-y-auto">
                    {[...new Set(clinicsData.map(clinic => clinic.state))].filter(Boolean).map((state) => (
                      <div
                        key={state}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleStateSelect(state)}
                      >
                        {state}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Filter By Suburb */}
              <div className="relative" ref={suburbRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Filter By Suburb:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleSuburbDropdown}
                >
                  <span className={filterBySuburb ? 'text-gray-900' : 'text-gray-500'}>
                    {filterBySuburb || 'Please Select'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showSuburbDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-32 overflow-y-auto">
                    {[...new Set(clinicsData.map(clinic => clinic.suburb))].filter(Boolean).map((suburb) => (
                      <div
                        key={suburb}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleSuburbSelect(suburb)}
                      >
                        {suburb}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Filter By Country */}
              <div className="relative" ref={countryRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Filter By Country:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleCountryDropdown}
                >
                  <span className={filterByCountry ? 'text-gray-900' : 'text-gray-500'}>
                    {filterByCountry || 'Please Select'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showCountryDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-32 overflow-y-auto">
                    {[...new Set(clinicsData.map(clinic => clinic.country))].filter(Boolean).map((country) => (
                      <div
                        key={country}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleCountrySelect(country)}
                      >
                        {country}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sort By */}
              <div className="relative" ref={sortRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Sort By:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleSortDropdown}
                >
                  <span className={sortBy ? 'text-gray-900' : 'text-gray-500'}>
                    {sortBy || 'Sort Options'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showSortDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
                    {['Name (A-Z)', 'Name (Z-A)', 'Date Added', 'Postcode'].map((option) => (
                      <div
                        key={option}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleSortSelect(option)}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reset Filters Button */}
              <div className="flex justify-end">
                <button
                  onClick={resetFilters}
                  className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-xs flex items-center gap-1"
                >
                  <X className="h-3 w-3" />
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Clinic List (75%) */}
        <div className="w-3/4 bg-gray-50 p-6">
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-4">
              {/* Loading State */}
              {loading && (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">Loading clinics...</span>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                  <div className="flex">
                    <div className="text-red-700 text-sm">
                      <strong>Error:</strong> {error}
                      <button 
                        onClick={fetchClinics}
                        className="ml-2 text-red-600 underline hover:text-red-800"
                      >
                        Try again
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Clinics List */}
              {!loading && !error && (
                <div className="space-y-2">
                  {currentClinics.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No clinics found matching your criteria.
                    </div>
                  ) : (
                    currentClinics.map((clinic) => (
                      <div key={clinic.id} className="border border-gray-200 rounded-md">
                        {/* Clinic Name Row */}
                        <div className={`flex justify-between items-center p-3 transition-colors ${
                          expandedClinic === clinic.id 
                            ? 'bg-gradient-to-r from-blue-50 to-indigo-100 border-b border-blue-200' 
                            : 'hover:bg-gray-50'
                        }`}>
                    <div className="flex-1">
                            <div className={`flex items-center text-sm font-medium ${
                              expandedClinic === clinic.id ? 'text-blue-900' : 'text-gray-900'
                            }`}>
                              <Building className={`h-4 w-4 mr-2 ${
                                expandedClinic === clinic.id ? 'text-blue-700' : 'text-gray-600'
                              }`} />
                              {clinic.clinic_name}
                            </div>
                    </div>
                    <button
                      onClick={() => handleClinicAction(clinic.id)}
                      className={`w-7 h-7 text-white rounded-md transition-colors flex items-center justify-center ${
                        expandedClinic === clinic.id 
                          ? 'bg-blue-700 hover:bg-blue-800' 
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                            {expandedClinic === clinic.id ? (
                              <Minus className="h-3 w-3" />
                            ) : (
                      <Plus className="h-3 w-3" />
                            )}
                    </button>
                  </div>

                        {/* Collapsible Clinic Details */}
                        {expandedClinic === clinic.id && (
                          <div className="border-t border-gray-200 bg-gray-100">
                            <div className="p-3 space-y-4">
                              
                              {/* Clinic Details Section */}
                              <div className="bg-white rounded-lg border border-gray-200">
                                <div className="bg-blue-600 text-white px-3 py-1.5 rounded-t-lg">
                                  <h3 className="text-xs font-medium flex items-center">
                                    <Building className="h-3 w-3 mr-2" />
                                    Clinic Details
                                  </h3>
                                </div>
                                <div className="p-3">
                                  {/* First Row: Name, Address, Suburb, State, Postcode */}
                                  <div className="grid grid-cols-5 gap-2 mb-3">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Clinic Name</label>
                                      <input
                                        type="text"
                                        value={
                                          editingSection === 'clinic-details' && editingClinicId === clinic.id
                                            ? editFormData.clinic_name || ''
                                            : clinic.clinic_name || ''
                                        }
                                        onChange={(e) => editingSection === 'clinic-details' && editingClinicId === clinic.id && updateEditFormData('clinic_name', e.target.value)}
                                        readOnly={!(editingSection === 'clinic-details' && editingClinicId === clinic.id)}
                                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                          editingSection === 'clinic-details' && editingClinicId === clinic.id
                                            ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                            : 'bg-gray-50'
                                        }`}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Clinic Address</label>
                                      <input
                                        type="text"
                                        value={
                                          editingSection === 'clinic-details' && editingClinicId === clinic.id
                                            ? editFormData.clinic_address || ''
                                            : clinic.clinic_address || ''
                                        }
                                        onChange={(e) => editingSection === 'clinic-details' && editingClinicId === clinic.id && updateEditFormData('clinic_address', e.target.value)}
                                        readOnly={!(editingSection === 'clinic-details' && editingClinicId === clinic.id)}
                                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                          editingSection === 'clinic-details' && editingClinicId === clinic.id
                                            ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                            : 'bg-gray-50'
                                        }`}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Suburb</label>
                                      {editingSection === 'clinic-details' && editingClinicId === clinic.id ? (
                                        <>
                                          <input
                                            type="text"
                                            list={`suburb-list-${clinic.id}`}
                                            value={editFormData.suburb || ''}
                                            onChange={(e) => updateEditFormData('suburb', e.target.value)}
                                            placeholder="Type or select suburb"
                                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                          />
                                          <datalist id={`suburb-list-${clinic.id}`}>
                                            {getUniqueSuburbs.map((suburb) => (
                                              <option key={suburb} value={suburb} />
                                            ))}
                                          </datalist>
                                        </>
                                      ) : (
                                        <input
                                          type="text"
                                          value={clinic.suburb || ''}
                                          readOnly
                                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-gray-50"
                                        />
                                      )}
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                                      {editingSection === 'clinic-details' && editingClinicId === clinic.id ? (
                                        <>
                                          <input
                                            type="text"
                                            list={`state-list-${clinic.id}`}
                                            value={editFormData.state || ''}
                                            onChange={(e) => updateEditFormData('state', e.target.value)}
                                            placeholder="Type or select state"
                                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                          />
                                          <datalist id={`state-list-${clinic.id}`}>
                                            {getUniqueStates.map((state) => (
                                              <option key={state} value={state} />
                                            ))}
                                          </datalist>
                                        </>
                                      ) : (
                                        <input
                                          type="text"
                                          value={clinic.state || ''}
                                          readOnly
                                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-gray-50"
                                        />
                                      )}
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Postcode</label>
                                      {editingSection === 'clinic-details' && editingClinicId === clinic.id ? (
                                        <>
                                          <input
                                            type="text"
                                            list={`postcode-list-${clinic.id}`}
                                            value={editFormData.postcode || ''}
                                            onChange={(e) => updateEditFormData('postcode', e.target.value)}
                                            placeholder="Type or select postcode"
                                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                          />
                                          <datalist id={`postcode-list-${clinic.id}`}>
                                            {getUniquePostcodes.map((postcode) => (
                                              <option key={postcode} value={postcode} />
                                            ))}
                                          </datalist>
                                        </>
                                      ) : (
                                        <input
                                          type="text"
                                          value={clinic.postcode || ''}
                                          readOnly
                                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-gray-50"
                                        />
                                      )}
                                    </div>
              </div>

                                  {/* Second Row: Country, Phone, Email, Services */}
                                  <div className="grid grid-cols-4 gap-2">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Country</label>
                                      {editingSection === 'clinic-details' && editingClinicId === clinic.id ? (
                                        <>
                                          <input
                                            type="text"
                                            list={`country-list-${clinic.id}`}
                                            value={editFormData.country || ''}
                                            onChange={(e) => updateEditFormData('country', e.target.value)}
                                            placeholder="Type or select country"
                                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                          />
                                          <datalist id={`country-list-${clinic.id}`}>
                                            {getUniqueCountries.map((country) => (
                                              <option key={country} value={country} />
                                            ))}
                                          </datalist>
                                        </>
                                      ) : (
                                        <input
                                          type="text"
                                          value={clinic.country || ''}
                                          readOnly
                                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-gray-50"
                                        />
                                      )}
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Clinic Phone</label>
                                      <input
                                        type="text"
                                        value={
                                          editingSection === 'clinic-details' && editingClinicId === clinic.id
                                            ? editFormData.phone || ''
                                            : clinic.phone || ''
                                        }
                                        onChange={(e) => editingSection === 'clinic-details' && editingClinicId === clinic.id && updateEditFormData('phone', e.target.value)}
                                        readOnly={!(editingSection === 'clinic-details' && editingClinicId === clinic.id)}
                                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                          editingSection === 'clinic-details' && editingClinicId === clinic.id
                                            ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                            : 'bg-gray-50'
                                        }`}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Clinic Email</label>
                                      <input
                                        type="text"
                                        value={
                                          editingSection === 'clinic-details' && editingClinicId === clinic.id
                                            ? editFormData.email || ''
                                            : clinic.email || ''
                                        }
                                        onChange={(e) => editingSection === 'clinic-details' && editingClinicId === clinic.id && updateEditFormData('email', e.target.value)}
                                        readOnly={!(editingSection === 'clinic-details' && editingClinicId === clinic.id)}
                                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                          editingSection === 'clinic-details' && editingClinicId === clinic.id
                                            ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                            : 'bg-gray-50'
                                        }`}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Clinic Services</label>
                                      <input
                                        type="text"
                                        value={
                                          editingSection === 'clinic-details' && editingClinicId === clinic.id
                                            ? editFormData.services || ''
                                            : clinic.services || ''
                                        }
                                        onChange={(e) => editingSection === 'clinic-details' && editingClinicId === clinic.id && updateEditFormData('services', e.target.value)}
                                        readOnly={!(editingSection === 'clinic-details' && editingClinicId === clinic.id)}
                                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                          editingSection === 'clinic-details' && editingClinicId === clinic.id
                                            ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                            : 'bg-gray-50'
                                        }`}
                                      />
                                    </div>
                                  </div>

                                  <div className="flex justify-end mt-3 space-x-2">
                                    {editingSection === 'clinic-details' && editingClinicId === clinic.id ? (
                                      <>
                                        <button 
                                          onClick={cancelEdit}
                                          disabled={saveLoading}
                                          className="px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                                        >
                                          Cancel
                                        </button>
                                        <button 
                                          onClick={() => saveChanges('clinic-details', clinic.id)}
                                          disabled={saveLoading}
                                          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
                                        >
                                          {saveLoading ? (
                                            <>
                                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                              Saving...
                                            </>
                                          ) : (
                                            'Save'
                                          )}
                                        </button>
                                      </>
                                    ) : (
                                      <button 
                                        onClick={() => startEdit('clinic-details', clinic.id, {
                                          clinic_name: clinic.clinic_name || '',
                                          clinic_address: clinic.clinic_address || '',
                                          suburb: clinic.suburb || '',
                                          state: clinic.state || '',
                                          postcode: clinic.postcode || '',
                                          country: clinic.country || '',
                                          phone: clinic.phone || '',
                                          email: clinic.email || '',
                                          services: clinic.services || ''
                                        })}
                                        className="px-3 py-1.5 text-xs text-blue-600 hover:text-blue-800"
                                      >
                                        Edit Record
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Contact Details Section */}
                              <div className="bg-white rounded-lg border border-gray-200">
                                <div className="bg-blue-600 text-white px-3 py-1.5">
                                  <h3 className="text-xs font-medium flex items-center">
                                    <Contact className="h-3 w-3 mr-2" />
                                    Contact Details
                                  </h3>
                                </div>
                                <div className="p-3">
                                  <div className="grid grid-cols-3 gap-2 mb-3">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Primary Contact</label>
                                      <input
                                        type="text"
                                        value={
                                          editingSection === 'contact-details' && editingClinicId === clinic.id
                                            ? editFormData.primary_contact_name || ''
                                            : clinic.primary_contact_name || ''
                                        }
                                        onChange={(e) => editingSection === 'contact-details' && editingClinicId === clinic.id && updateEditFormData('primary_contact_name', e.target.value)}
                                        readOnly={!(editingSection === 'contact-details' && editingClinicId === clinic.id)}
                                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                          editingSection === 'contact-details' && editingClinicId === clinic.id
                                            ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                            : 'bg-gray-50'
                                        }`}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Primary Contact Phone</label>
                                      <input
                                        type="text"
                                        value={
                                          editingSection === 'contact-details' && editingClinicId === clinic.id
                                            ? editFormData.primary_contact_phone || ''
                                            : clinic.primary_contact_phone || ''
                                        }
                                        onChange={(e) => editingSection === 'contact-details' && editingClinicId === clinic.id && updateEditFormData('primary_contact_phone', e.target.value)}
                                        readOnly={!(editingSection === 'contact-details' && editingClinicId === clinic.id)}
                                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                          editingSection === 'contact-details' && editingClinicId === clinic.id
                                            ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                            : 'bg-gray-50'
                                        }`}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Primary Contact Email</label>
                                      <input
                                        type="text"
                                        value={
                                          editingSection === 'contact-details' && editingClinicId === clinic.id
                                            ? editFormData.primary_contact_email || ''
                                            : clinic.primary_contact_email || ''
                                        }
                                        onChange={(e) => editingSection === 'contact-details' && editingClinicId === clinic.id && updateEditFormData('primary_contact_email', e.target.value)}
                                        readOnly={!(editingSection === 'contact-details' && editingClinicId === clinic.id)}
                                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                          editingSection === 'contact-details' && editingClinicId === clinic.id
                                            ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                            : 'bg-gray-50'
                                        }`}
                                      />
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-4 gap-2">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Accounts Contact</label>
                                      <input
                                        type="text"
                                        value={
                                          editingSection === 'contact-details' && editingClinicId === clinic.id
                                            ? editFormData.accounts_contact_name || ''
                                            : clinic.accounts_contact_name || ''
                                        }
                                        onChange={(e) => editingSection === 'contact-details' && editingClinicId === clinic.id && updateEditFormData('accounts_contact_name', e.target.value)}
                                        readOnly={!(editingSection === 'contact-details' && editingClinicId === clinic.id)}
                                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                          editingSection === 'contact-details' && editingClinicId === clinic.id
                                            ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                            : 'bg-gray-50'
                                        }`}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Accounts Contact Phone</label>
                                      <input
                                        type="text"
                                        value={
                                          editingSection === 'contact-details' && editingClinicId === clinic.id
                                            ? editFormData.accounts_contact_phone || ''
                                            : clinic.accounts_contact_phone || ''
                                        }
                                        onChange={(e) => editingSection === 'contact-details' && editingClinicId === clinic.id && updateEditFormData('accounts_contact_phone', e.target.value)}
                                        readOnly={!(editingSection === 'contact-details' && editingClinicId === clinic.id)}
                                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                          editingSection === 'contact-details' && editingClinicId === clinic.id
                                            ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                            : 'bg-gray-50'
                                        }`}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Accounts Contact Email</label>
                                      <input
                                        type="text"
                                        value={
                                          editingSection === 'contact-details' && editingClinicId === clinic.id
                                            ? editFormData.accounts_contact_email || ''
                                            : clinic.accounts_contact_email || ''
                                        }
                                        onChange={(e) => editingSection === 'contact-details' && editingClinicId === clinic.id && updateEditFormData('accounts_contact_email', e.target.value)}
                                        readOnly={!(editingSection === 'contact-details' && editingClinicId === clinic.id)}
                                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                          editingSection === 'contact-details' && editingClinicId === clinic.id
                                            ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                            : 'bg-gray-50'
                                        }`}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Billing Email</label>
                                      <input
                                        type="text"
                                        value={
                                          editingSection === 'contact-details' && editingClinicId === clinic.id
                                            ? editFormData.billing_email || ''
                                            : clinic.billing_email || ''
                                        }
                                        onChange={(e) => editingSection === 'contact-details' && editingClinicId === clinic.id && updateEditFormData('billing_email', e.target.value)}
                                        readOnly={!(editingSection === 'contact-details' && editingClinicId === clinic.id)}
                                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                          editingSection === 'contact-details' && editingClinicId === clinic.id
                                            ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                            : 'bg-gray-50'
                                        }`}
                                      />
                                    </div>
                                  </div>
                                  <div className="flex justify-end mt-3 space-x-2">
                                    {editingSection === 'contact-details' && editingClinicId === clinic.id ? (
                                      <>
                                        <button 
                                          onClick={cancelEdit}
                                          disabled={saveLoading}
                                          className="px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                                        >
                                          Cancel
                                        </button>
                                        <button 
                                          onClick={() => saveChanges('contact-details', clinic.id)}
                                          disabled={saveLoading}
                                          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
                                        >
                                          {saveLoading ? (
                                            <>
                                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                              Saving...
                                            </>
                                          ) : (
                                            'Save'
                                          )}
                                        </button>
                                      </>
                                    ) : (
                                      <button 
                                        onClick={() => startEdit('contact-details', clinic.id, {
                                          primary_contact_name: clinic.primary_contact_name || '',
                                          primary_contact_phone: clinic.primary_contact_phone || '',
                                          primary_contact_email: clinic.primary_contact_email || '',
                                          accounts_contact_name: clinic.accounts_contact_name || '',
                                          accounts_contact_phone: clinic.accounts_contact_phone || '',
                                          accounts_contact_email: clinic.accounts_contact_email || '',
                                          billing_email: clinic.billing_email || ''
                                        })}
                                        className="px-3 py-1.5 text-xs text-blue-600 hover:text-blue-800"
                                      >
                                        Edit Record
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Operating Hours Section */}
                              <OperatingHoursSection 
                                clinic={clinic} 
                                editingSection={editingSection}
                                editingClinicId={editingClinicId}
                                editFormData={editFormData}
                                updateEditFormData={updateEditFormData}
                                startEdit={startEdit}
                                cancelEdit={cancelEdit}
                                saveChanges={saveChanges}
                                saveLoading={saveLoading}
                              />

                              {/* Clinicians Section */}
                              <ClinicianCarousel clinicId={clinic.id} />

                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Pagination */}
              {!loading && !error && filteredClinics.length > 0 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredClinics.length)} of {filteredClinics.length} clinics
                    {filteredClinics.length !== clinicsData.length && (
                      <span className="text-blue-600"> (filtered from {clinicsData.length} total)</span>
                    )}
                </div>
                
                  {totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  
                  <div className="flex space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageClick(pageNumber)}
                        className={`px-3 py-2 text-sm rounded-md transition-colors ${
                          currentPage === pageNumber
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
                  )}
              </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Clinic Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeAddModal}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="w-full">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                        <Building className="h-5 w-5 mr-2 text-blue-600" />
                        Add New Clinic
                      </h3>
                      <button
                        onClick={closeAddModal}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="space-y-6">
                      {/* Clinic Details Section */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                          <Building className="h-4 w-4 mr-2 text-blue-600" />
                          Clinic Details
                        </h4>
                        
                        {/* First Row */}
                        <div className="grid grid-cols-5 gap-3 mb-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Clinic Name *</label>
                            <input
                              type="text"
                              value={addClinicData.clinic_name}
                              onChange={(e) => updateAddClinicData('clinic_name', e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Clinic Address *</label>
                            <input
                              type="text"
                              value={addClinicData.clinic_address}
                              onChange={(e) => updateAddClinicData('clinic_address', e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Suburb</label>
                            <input
                              type="text"
                              list="add-suburb-list"
                              value={addClinicData.suburb}
                              onChange={(e) => updateAddClinicData('suburb', e.target.value)}
                              placeholder="Type or select suburb"
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <datalist id="add-suburb-list">
                              {getUniqueSuburbs.map((suburb) => (
                                <option key={suburb} value={suburb} />
                              ))}
                            </datalist>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                            <input
                              type="text"
                              list="add-state-list"
                              value={addClinicData.state}
                              onChange={(e) => updateAddClinicData('state', e.target.value)}
                              placeholder="Type or select state"
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <datalist id="add-state-list">
                              {getUniqueStates.map((state) => (
                                <option key={state} value={state} />
                              ))}
                            </datalist>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Postcode</label>
                            <input
                              type="text"
                              list="add-postcode-list"
                              value={addClinicData.postcode}
                              onChange={(e) => updateAddClinicData('postcode', e.target.value)}
                              placeholder="Type or select postcode"
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <datalist id="add-postcode-list">
                              {getUniquePostcodes.map((postcode) => (
                                <option key={postcode} value={postcode} />
                              ))}
                            </datalist>
                          </div>
                        </div>

                        {/* Second Row */}
                        <div className="grid grid-cols-4 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Country</label>
                            <input
                              type="text"
                              list="add-country-list"
                              value={addClinicData.country}
                              onChange={(e) => updateAddClinicData('country', e.target.value)}
                              placeholder="Type or select country"
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <datalist id="add-country-list">
                              {getUniqueCountries.map((country) => (
                                <option key={country} value={country} />
                              ))}
                            </datalist>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Clinic Phone</label>
                            <input
                              type="text"
                              value={addClinicData.phone}
                              onChange={(e) => updateAddClinicData('phone', e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Clinic Email</label>
                            <input
                              type="email"
                              value={addClinicData.email}
                              onChange={(e) => updateAddClinicData('email', e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Services</label>
                            <input
                              type="text"
                              value={addClinicData.services}
                              onChange={(e) => updateAddClinicData('services', e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Contact Details Section */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                          <Contact className="h-4 w-4 mr-2 text-blue-600" />
                          Contact Details
                        </h4>
                        
                        {/* Primary Contact Row */}
                        <div className="grid grid-cols-3 gap-3 mb-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Primary Contact</label>
                            <input
                              type="text"
                              value={addClinicData.primary_contact_name}
                              onChange={(e) => updateAddClinicData('primary_contact_name', e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Primary Contact Phone</label>
                            <input
                              type="text"
                              value={addClinicData.primary_contact_phone}
                              onChange={(e) => updateAddClinicData('primary_contact_phone', e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Primary Contact Email</label>
                            <input
                              type="email"
                              value={addClinicData.primary_contact_email}
                              onChange={(e) => updateAddClinicData('primary_contact_email', e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>

                        {/* Accounts Contact Row */}
                        <div className="grid grid-cols-4 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Accounts Contact</label>
                            <input
                              type="text"
                              value={addClinicData.accounts_contact_name}
                              onChange={(e) => updateAddClinicData('accounts_contact_name', e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Accounts Contact Phone</label>
                            <input
                              type="text"
                              value={addClinicData.accounts_contact_phone}
                              onChange={(e) => updateAddClinicData('accounts_contact_phone', e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Accounts Contact Email</label>
                            <input
                              type="email"
                              value={addClinicData.accounts_contact_email}
                              onChange={(e) => updateAddClinicData('accounts_contact_email', e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Billing Email</label>
                            <input
                              type="email"
                              value={addClinicData.billing_email}
                              onChange={(e) => updateAddClinicData('billing_email', e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Public Holiday Settings */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                          <Settings className="h-4 w-4 mr-2 text-blue-600" />
                          Public Holiday Settings
                        </h4>
                        
                        <div className="flex space-x-6">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="open_public_holidays"
                              checked={addClinicData.open_public_holidays}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  updateAddClinicData('open_public_holidays', true);
                                  updateAddClinicData('closed_public_holidays', false);
                                } else {
                                  updateAddClinicData('open_public_holidays', false);
                                }
                              }}
                              className="rounded"
                            />
                            <label htmlFor="open_public_holidays" className="ml-2 text-sm text-gray-700">
                              Open Public Holidays
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="closed_public_holidays"
                              checked={addClinicData.closed_public_holidays}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  updateAddClinicData('closed_public_holidays', true);
                                  updateAddClinicData('open_public_holidays', false);
                                } else {
                                  updateAddClinicData('closed_public_holidays', false);
                                }
                              }}
                              className="rounded"
                            />
                            <label htmlFor="closed_public_holidays" className="ml-2 text-sm text-gray-700">
                              Closed Public Holidays
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleAddClinic}
                  disabled={addLoading || !addClinicData.clinic_name || !addClinicData.clinic_address}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    'Create Clinic'
                  )}
                </button>
                <button
                  type="button"
                  onClick={closeAddModal}
                  disabled={addLoading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 space-y-2 z-50">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-center px-4 py-3 rounded-lg shadow-lg transform transition-all duration-300 ease-in-out ${
              toast.type === 'success'
                ? 'bg-green-500 text-white'
                : 'bg-red-500 text-white'
            }`}
          >
            {toast.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />
            )}
            <span className="text-sm font-medium">{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              className="ml-3 flex-shrink-0 text-white hover:text-gray-200 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminClinicsPage;
